'use client';

import { BigNum, PublicKey } from '@drift-labs/sdk';
import { ReactNode, useEffect, useState } from 'react';
import TableV2 from 'src/components/Tables/TableV2';
import TableStateWrapper from 'src/components/TableStateWrapper';
import { useSettlePnl } from 'src/hooks/driftActions/settlement/useSettlePnl';
import ExchangeHistoryClient from 'src/utils/exchangeHistoryClient';
import { openUrl } from 'src/utils/browser';
import { abbreviateAddress } from '@drift/common';
import { cj } from 'src/utils/classNames';
import { TabOption } from 'src/components/primitives/Tabs/types';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import Button from 'src/components/primitives/Button/Button';

type PnlUser = {
	userPubKey: PublicKey;
	pnl: BigNum;
};

type TabOptionValue = 'gain' | 'loss';

const tabs: TabOption<TabOptionValue>[] = [
	{
		label: 'Top Negative Accounts',
		value: 'loss',
	},
	{
		label: 'Top Positive Accounts',
		value: 'gain',
	},
];

export const PnlLeaderboardPanel = (props: {
	marketIndex: number;
	poolBalanceDisplay: string;
	goBackComponent?: ReactNode;
}) => {
	const { settlePnl } = useSettlePnl();

	const [selectedTab, setSelectedTab] = useState<TabOptionValue>('loss');
	const [gainers, setGainers] = useState<PnlUser[]>([]);
	const [losers, setLosers] = useState<PnlUser[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [settledUsers, setSettledUsers] = useState<string[]>([]);

	const settleUser = async (userPubKey: PublicKey) => {
		const success = await settlePnl(props.marketIndex, userPubKey);
		// endpoint does not update instantaneously, so if user settles somebody successfully,
		// filter it out from results (at least while they're still in the panel)
		if (success) {
			setSettledUsers(settledUsers.concat(userPubKey.toString()));
		}
	};

	useEffect(() => {
		setIsLoading(true);
		ExchangeHistoryClient.getUnsettledPnlUsersForMarket(props.marketIndex)
			.then((result) => {
				setGainers(
					result.gainers
						.filter(
							(gainer) => !settledUsers.includes(gainer.userPubKey.toString())
						)
						.slice(0, 15)
				);
				setLosers(
					result.losers
						.filter(
							(loser) => !settledUsers.includes(loser.userPubKey.toString())
						)
						.slice(0, 15)
				);
				setIsLoading(false);
			})
			.catch((_e) => {
				setIsLoading(false);
			});
	}, [props.marketIndex, settledUsers]);

	return (
		<div className="flex h-full w-full flex-col gap-y-lg">
			{props.goBackComponent && (
				<div className="w-full justify-start">{props.goBackComponent}</div>
			)}
			<div className="flex w-full flex-col gap-y-sm rounded-sm border border-stroke-divider bg-input-bg p-lg">
				<span className="typo-b3 text-text-secondary">P&L Pool Balances</span>
				<span className="typo-t1 text-text-default">
					{props.poolBalanceDisplay}
				</span>
			</div>
			<div className="w-full text-text-secondary">
				<span className="typo-b4">
					You can settle negative P&L balances from other accounts to boost the
					P&L pool. Balances may not update immediately.
				</span>
			</div>
			<div className="w-full">
				<Tabs
					size="sm"
					options={tabs}
					activeTab={selectedTab}
					onTabChange={setSelectedTab}
					indicatorTrack
				/>
			</div>
			{selectedTab === 'loss' ? (
				<PnlUsersTable
					marketIndex={props.marketIndex}
					users={losers}
					settleAction={settleUser}
					loading={isLoading}
					positive={false}
					showSettle
				/>
			) : (
				<PnlUsersTable
					marketIndex={props.marketIndex}
					users={gainers}
					settleAction={settleUser}
					loading={isLoading}
					showSettle={false}
					positive
				/>
			)}
		</div>
	);
};

const PnlUsersTable = (props: {
	marketIndex: number;
	users: PnlUser[];
	positive: boolean;
	showSettle: boolean;
	settleAction: (userPubKey: PublicKey) => void;
	loading?: boolean;
}) => {
	const headers = props.positive
		? ['Account', 'Positive P&L']
		: ['Account', 'Negative P&L'];

	const tableGrid = `minmax(50px,10fr) minmax(50px,10fr) ${
		props.showSettle ? 'minmax(50px,10fr)' : ''
	}`;

	if (props.showSettle) {
		headers.push('Action');
	}

	return (
		<TableStateWrapper
			records={props.users}
			emptyStateText="Accounts could not be fetched, try again later."
			loading={props.loading}
		>
			<TableV2.Skeleton
				className="flex rounded-sm"
				top={
					<TableV2.HeaderRow
						grid={tableGrid}
						className={cj('w-full', props.showSettle && 'pr-4')}
						lastColumnJustify="end"
					>
						{headers.map((label, index) => (
							<TableV2.HeaderCell
								className={cj(
									'text-text-secondary',
									index === headers.length - 1 && 'justify-end',
									index !== 0 &&
										index === headers.length - 2 &&
										'justify-center'
								)}
								key={`header_${label}`.replace(/ /g, '')}
							>
								{label}
							</TableV2.HeaderCell>
						))}
					</TableV2.HeaderRow>
				}
				middle={
					<div className="flex thin-scroll size-full flex-col overflow-y-auto">
						{props.users.map((pnlUser, index) => {
							return (
								<TableV2.BodyRow
									key={`${index}_${pnlUser.userPubKey.toString()}`}
									grid={tableGrid}
									lastColumnJustify="end"
									className="h-10"
								>
									<TableV2.BodyCell
										className="h-10 items-center hover:cursor-pointer"
										key={`pnl_account_${props.marketIndex}_${
											props.positive ? 'pos' : 'neg'
										}_${index}`}
										onClick={() => {
											openUrl(
												`/portfolio/trade?userAccount=${pnlUser.userPubKey.toString()}`
											);
										}}
									>
										{abbreviateAddress(pnlUser.userPubKey)}
									</TableV2.BodyCell>
									<TableV2.BodyCell
										className={cj(
											'items-center',
											!props.showSettle ? 'justify-end' : 'justify-center',
											props.positive
												? 'text-text-positive-green-button'
												: 'text-text-negative-red-button'
										)}
										key={`pnl_value_${props.marketIndex}_${
											props.positive ? 'pos' : 'neg'
										}_${index}`}
									>
										{pnlUser.pnl.toNotional()}
									</TableV2.BodyCell>
									{props.showSettle && (
										<TableV2.BodyCell
											className="items-center justify-end"
											key={`settle_user_${props.marketIndex}_${
												props.positive ? 'pos' : 'neg'
											}_${index}`}
										>
											<Button
												size="sm"
												variant="secondary"
												onClick={() => props.settleAction(pnlUser.userPubKey)}
											>
												Settle
											</Button>
										</TableV2.BodyCell>
									)}
								</TableV2.BodyRow>
							);
						})}
					</div>
				}
			/>
		</TableStateWrapper>
	);
};
